import { useContext, type ReactNode } from "react";
import { Check, Mail, RefreshCw } from "lucide-react";

import { Button } from "@nexus/ui";
import { PublicConfigContext } from "@/contexts/PublicConfigContext";
import type { Account } from "@/hooks/useAccount";
import { cn } from "@/lib/utils";

type StepState = "done" | "current" | "upcoming";

const MARKER_CLASSES: Record<StepState, string> = {
  done: "border border-status-ok/40 bg-status-ok-bg text-status-ok-text",
  current: "border-2 border-brand-cyan bg-surface text-interaction-text",
  upcoming: "border border-border bg-surface text-muted",
};

const STATUS_CLASSES: Record<StepState, string> = {
  done: "text-status-ok-text",
  current: "font-medium text-interaction-text",
  upcoming: "text-muted",
};

function SetupStep({
  index,
  state,
  title,
  status,
  last = false,
  children,
}: {
  index: number;
  state: StepState;
  title: string;
  status?: string;
  last?: boolean;
  children?: ReactNode;
}) {
  return (
    <li
      aria-current={state === "current" ? "step" : undefined}
      className={cn("relative grid grid-cols-[2rem_minmax(0,1fr)] gap-x-4", !last && "pb-7")}
    >
      {last ? null : (
        <span
          aria-hidden
          className={cn(
            "absolute bottom-1 left-4 top-10 w-px -translate-x-1/2",
            state === "done" ? "bg-status-ok/40" : "bg-border",
          )}
        />
      )}
      <span
        aria-hidden
        className={cn(
          "flex size-8 items-center justify-center rounded-full text-sm font-semibold",
          MARKER_CLASSES[state],
        )}
      >
        {state === "done" ? <Check className="size-4" strokeWidth={3} /> : index}
      </span>
      <div className="min-w-0">
        <div className="flex min-h-8 flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <h3
            className={cn(
              "text-sm font-semibold",
              state === "upcoming" ? "text-secondary" : "text-primary",
            )}
          >
            {title}
          </h3>
          {status ? <span className={cn("text-xs", STATUS_CLASSES[state])}>{status}</span> : null}
        </div>
        {children}
      </div>
    </li>
  );
}

/** Home panel for a signed-in account that has no tenant access yet. */
export function HomeAccessSetup({
  account,
  onCheckAgain,
  checking,
}: {
  account: Pick<Account, "email" | "account_id">;
  onCheckAgain: () => void;
  checking: boolean;
}) {
  const { supportEmail } = useContext(PublicConfigContext);
  const subject = encodeURIComponent("Nexus account access configuration");
  const body = encodeURIComponent(
    `Hello,\n\nPlease configure my Nexus account access.\n\nEmail: ${account.email}\nAccount reference: ${account.account_id}\nOrganization or services I need to access: \n\nThank you.`,
  );

  return (
    <section
      aria-labelledby="home-access-setup"
      className="w-full max-w-2xl rounded-panel border border-border bg-surface text-primary"
    >
      <h2 id="home-access-setup" className="px-6 pt-6 text-lg font-semibold sm:px-8">
        Your account is almost ready
      </h2>

      <ol aria-label="Account setup" className="px-6 pt-5 pb-6 sm:px-8">
        <SetupStep index={1} state="done" title="Sign in" status="Completed">
          <p className="mt-0.5 break-all text-sm text-secondary">{account.email}</p>
        </SetupStep>

        <SetupStep index={2} state="current" title="Request access" status="Action needed">
          <p className="mt-0.5 text-sm leading-6 text-secondary">
            {supportEmail
              ? "Tell support which organization you work for."
              : "Ask your Nexus administrator for access, quoting this reference."}
          </p>
          {supportEmail ? (
            <div className="mt-3 flex flex-col items-start gap-x-4 gap-y-2 sm:flex-row sm:flex-wrap sm:items-center">
              <a
                href={`mailto:${encodeURIComponent(supportEmail)}?subject=${subject}&body=${body}`}
                className="density-control-md inline-flex max-w-full items-center gap-2 rounded-control bg-action-primary px-4 text-sm font-medium text-action-primary-ink transition-colors hover:bg-action-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                <Mail className="size-4 shrink-0" aria-hidden />
                Email support
              </a>
              <span className="min-w-0 break-all text-sm text-secondary">{supportEmail}</span>
            </div>
          ) : (
            <code className="mt-2 inline-block select-all break-all rounded bg-surface-elevated px-1.5 py-0.5 font-mono text-xs text-secondary">
              {account.account_id}
            </code>
          )}
        </SetupStep>

        <SetupStep index={3} state="upcoming" title="See your dashboards" last>
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 mt-1"
            onClick={onCheckAgain}
            loading={checking}
            loadingLabel="Checking…"
          >
            <RefreshCw className="size-3.5" aria-hidden />
            Check again
          </Button>
        </SetupStep>
      </ol>
    </section>
  );
}
